import { apiGet, apiPost, apiDelete } from './client';

export interface BillingStatus {
  plan: string;
  subscription_id?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  payment_method?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  } | null;
  /** Dunning (Phase 4b.1): 'grace' = payment failed (asking for pay), 'suspended' = sites offline. */
  dunning_state?: string | null;
  /** MyStateMLS sign-ups: checkout carries no setup fee (gateway lib/site-preview.ts launchOfferFor). */
  setup_fee_waived?: boolean;
}

export interface CheckoutSession {
  checkout_url: string;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  // Gateway route is GET /billing/status (no /tenant prefix).
  return apiGet<BillingStatus>('/billing/status');
}

export async function createCheckoutSession(priceId: string, plan: string): Promise<CheckoutSession> {
  // Gateway route is POST /billing/checkout (no /tenant prefix). `plan` is the card pressed: the
  // gateway refuses a price it has under another plan, and stamps the plan on the subscription.
  return apiPost<CheckoutSession>('/billing/checkout', { price_id: priceId, plan });
}

export async function cancelSubscription(): Promise<{ cancelled: boolean }> {
  // Gateway route is POST /billing/cancel (no /tenant prefix).
  return apiPost<{ cancelled: boolean }>('/billing/cancel');
}

/**
 * Request to cancel (managed sites): the main owner is emailed a link; the gateway never returns
 * it. The link opens /confirm-cancel, and only its Confirm button cancels (at period end).
 */
export const requestCancel = () => apiPost<{ sent: boolean; email: string }>('/billing/cancel-request', {});

export interface CancelPreview { plan: string; plan_name: string; site: string; email: string }
export const previewCancel = (token: string) => apiGet<CancelPreview>('/billing/cancel-request/preview', { token });
export const confirmCancel = (token: string) => apiPost<{ cancelled: boolean }>('/billing/cancel-request/confirm', { token });

// ── Add-ons (AI Marketer, SEO Autopilot — lib/entitlements.ts) ─────────────────

export type AddonId = 'ai_marketer' | 'seo_autopilot';

export interface AddonsState {
  plan: string;
  /** Brokerage Growth: both included. */
  included: boolean;
  /** Agent IDX Growth: can buy them. Free/Essentials: must upgrade first. */
  purchasable: boolean;
  /** The plan's billing interval — add-ons bill with it. Null when unknown. */
  interval: 'month' | 'year' | null;
  /** At least one add-on can be bought right now (see each add-on's own `configured`). */
  configured: boolean;
  prices: { single: number; bundle: number; per: 'month' | 'year' };
  /** `configured`: this one can be bought now — its price (or the bundle's, when the other is
   *  on) is set up for the plan's interval, and that interval is known. */
  addons: Array<{ id: AddonId; label: string; active: boolean; purchased: boolean; configured: boolean }>;
}

export async function getAddons(): Promise<AddonsState> {
  return apiGet<AddonsState>('/billing/addons');
}

/** Add charges the prorated amount now; remove credits the unused time. */
export async function changeAddon(addon: AddonId, action: 'add' | 'remove'): Promise<{ changed: boolean; addons: AddonId[] }> {
  return apiPost<{ changed: boolean; addons: AddonId[] }>('/billing/addons', { addon, action });
}

// ── Payment methods (saved cards) ──────────────────────────────────────────────

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
}

export interface PaymentMethodList {
  payment_methods: PaymentMethod[];
  default_payment_method_id: string | null;
}

export async function listPaymentMethods(): Promise<PaymentMethodList> {
  return apiGet<PaymentMethodList>('/billing/payment-methods');
}

export async function createCardSetupIntent(): Promise<{ client_secret: string }> {
  return apiPost<{ client_secret: string }>('/billing/payment-methods/setup-intent');
}

export async function setDefaultPaymentMethod(id: string): Promise<{ default_payment_method_id: string }> {
  return apiPost<{ default_payment_method_id: string }>(`/billing/payment-methods/${id}/default`);
}

export async function removePaymentMethod(id: string): Promise<{ detached: boolean }> {
  return apiDelete<{ detached: boolean }>(`/billing/payment-methods/${id}`);
}

// ── Wallet top-up ──────────────────────────────────────────────────────────────

export async function createWalletTopup(amountUsd: number): Promise<{ client_secret: string; amount_usd: number }> {
  return apiPost<{ client_secret: string; amount_usd: number }>('/billing/wallet/topup', { amount_usd: amountUsd });
}

// ── Invoices ─────────────────────────────────────────────────────────────────────

export interface InvoiceView {
  id: string;
  number: string | null;
  status: string | null; // draft | open | paid | uncollectible | void
  created: string; // ISO
  period_start: string | null;
  period_end: string | null;
  amount_due_cents: number;
  amount_paid_cents: number;
  currency: string;
  hosted_invoice_url: string | null; // Stripe-hosted, viewable + printable
  invoice_pdf: string | null; // Stripe-hosted PDF download
  description: string | null;
}

/** The signed-in user's own invoices (Stripe-hosted view + PDF links). */
export async function getMyInvoices(): Promise<InvoiceView[]> {
  const { invoices } = await apiGet<{ invoices: InvoiceView[] }>('/billing/invoices');
  return invoices ?? [];
}
